import { useEffect, useMemo, useRef, useState } from 'react';
import { activePath, canUndoTurn, type ChatMessage } from '@voicer/shared';
import { useStore } from '../state/store';
import { controller } from '../conversation/controller';
import { ws } from '../ws/client';
import { ConversationBrowser } from './ConversationBrowser';
import { DocumentBrowser } from './DocumentBrowser';
import { DocumentViewer } from './DocumentViewer';
import { assistantDisplayText, MarkdownMessage } from './MarkdownMessage';
import { composerCanSend } from './composerEnabled';
import { pttActivateOnPointerDown, pttComposerLabel } from './pttComposerLabel';

const STAGE_LABEL: Record<string, string> = {
  intake: 'Early / intake',
  discovery: 'Discovery',
  trial_prep: 'Trial prep',
};

export function CaseWorkspace(): React.ReactNode {
  const session = useStore((s) => s.session);
  const streaming = useStore((s) => s.streaming);
  const cases = useStore((s) => s.cases);
  const activeCaseId = useStore((s) => s.activeCaseId);
  const selectedDocumentId = useStore((s) => s.selectedDocumentId);
  const arbiterState = useStore((s) => s.arbiterState);
  const mode = useStore((s) => s.settings.mode);
  const micReady = useStore((s) => s.audio.micReady);
  const pttHeld = useStore((s) => s.audio.pttHeld);
  const [text, setText] = useState('');
  const [browserOpen, setBrowserOpen] = useState(false);
  const [busy, setBusy] = useState<'new' | 'case' | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const prepareTimer = useRef<number | null>(null);

  const activeCase = cases.find((c) => c.id === activeCaseId) ?? null;

  const visible = useMemo(() => {
    if (!session) return [] as ChatMessage[];
    return activePath(session.messages, session.activeLeafId).filter((m) => m.role !== 'aside');
  }, [session]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [visible.length, streaming?.text, session?.activeLeafId]);

  useEffect(() => {
    return () => {
      if (prepareTimer.current !== null) window.clearTimeout(prepareTimer.current);
    };
  }, []);

  /** Speculative compile while typing so Send often hits a warm turn.prepare cache. */
  const onComposerChange = (value: string): void => {
    setText(value);
    if (prepareTimer.current !== null) window.clearTimeout(prepareTimer.current);
    const trimmed = value.trim();
    if (!trimmed || !activeCaseId) return;
    prepareTimer.current = window.setTimeout(() => {
      prepareTimer.current = null;
      void ws.rpc('turn.prepare', { text: trimmed }).catch(() => {});
    }, 400);
  };

  const assistantBusy =
    arbiterState === 'thinking' ||
    arbiterState === 'speaking' ||
    arbiterState === 'ducked' ||
    arbiterState === 'classifying' ||
    arbiterState === 'candidate';

  const canUndo = !!session && canUndoTurn(activePath(session.messages, session.activeLeafId)) && !busy && !streaming;
  const canForward = !!session?.forwardLeafId && !busy && !streaming && !assistantBusy;

  const [sendInFlight, setSendInFlight] = useState(false);
  const canSend = composerCanSend({ hasText: !!text.trim(), activeCaseId, sendInFlight });

  const send = async (): Promise<void> => {
    if (!canSend) return;
    if (prepareTimer.current !== null) {
      window.clearTimeout(prepareTimer.current);
      prepareTimer.current = null;
    }
    const value = text;
    const trimmed = value.trim();
    // Flush speculative compile immediately so turn.start often hits a warm cache
    // (do not await — overlap with sendText / turn.start).
    if (trimmed && activeCaseId) {
      void ws.rpc('turn.prepare', { text: trimmed }).catch(() => {});
    }
    setText('');
    setSendInFlight(true);
    try {
      await controller.sendText(value);
    } finally {
      setSendInFlight(false);
    }
  };

  const onCaseChange = async (caseId: string): Promise<void> => {
    if (!caseId || caseId === activeCaseId) return;
    setBusy('case');
    try {
      await controller.switchCase(caseId);
      useStore.getState().setSelectedDocumentId(null);
    } finally {
      setBusy(null);
    }
  };

  const doNew = async (): Promise<void> => {
    setBusy('new');
    try {
      await controller.newConversation({ preserveMemories: false, caseId: activeCaseId });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="case-workspace">
      <div className="chat-toolbar case-toolbar">
        <label className="case-select-wrap">
          <span className="dim case-select-label">Case</span>
          <select
            className="case-select"
            value={activeCaseId ?? ''}
            disabled={busy === 'case' || !cases.length}
            onChange={(e) => void onCaseChange(e.target.value)}
          >
            {!cases.length && <option value="">No cases loaded</option>}
            {cases.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} — {STAGE_LABEL[c.stage] ?? c.stage}
              </option>
            ))}
          </select>
        </label>
        {activeCase ? (
          <span className="dim case-meta" title={activeCase.synopsis}>
            {activeCase.matterNumber} · {activeCase.jurisdiction}
          </span>
        ) : null}
        <span className="spacer" />
        <button type="button" className="small" onClick={() => setBrowserOpen(true)} title="Past conversations for this case">
          History
        </button>
        <button type="button" className="small primary" onClick={() => void doNew()} disabled={busy === 'new' || !activeCaseId}>
          {busy === 'new' ? 'Starting…' : 'New chat'}
        </button>
        <div className="case-more-wrap">
          <button type="button" className="small" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen}>
            More
          </button>
          {menuOpen && (
            <div className="case-more-pop">
              <button
                type="button"
                className="small"
                disabled={!canUndo}
                onClick={() => {
                  setMenuOpen(false);
                  void controller.undoTurn();
                }}
              >
                Undo turn
              </button>
              <button
                type="button"
                className="small"
                disabled={!canForward}
                onClick={() => {
                  setMenuOpen(false);
                  void controller.forwardTurn();
                }}
              >
                Forward
              </button>
              <button
                type="button"
                className="small"
                disabled={!visible.some((m) => m.role === 'user') || !!streaming || assistantBusy}
                onClick={() => {
                  setMenuOpen(false);
                  void controller.regenerate();
                }}
              >
                Redo reply
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={selectedDocumentId ? 'case-body with-viewer' : 'case-body'}>
        <aside className="case-docs-pane">
          <DocumentBrowser />
        </aside>
        {selectedDocumentId ? (
          <section className="case-viewer-pane">
            <DocumentViewer />
          </section>
        ) : null}
        <section className="case-chat-pane">
          <div className="messages case-messages" ref={listRef}>
            {!visible.length && !streaming && (
              <div className="empty">
                <p>Ask about this matter — liability, medicals, discovery, strategy, or drafting.</p>
                <p className="dim">
                  {activeCase
                    ? `${activeCase.partyPlaintiff} v. ${activeCase.partyDefendant}. Documents are on the left; open one to expand the viewer.`
                    : 'Select a case to begin.'}
                </p>
              </div>
            )}
            {visible.map((m) => (
              <div key={m.id} className={`msg ${m.role}`}>
                <div className={`bubble case-bubble${m.role === 'assistant' ? ' md' : ''}`}>
                  {m.role === 'assistant' ? (
                    <MarkdownMessage text={assistantDisplayText(m.content, streaming, m.turnId)} />
                  ) : (
                    m.content || <span className="dim">…</span>
                  )}
                </div>
              </div>
            ))}
            {streaming && !visible.some((m) => m.turnId === streaming.turnId && m.role === 'assistant') && (
              <div className="msg assistant streaming">
                <div className="bubble case-bubble md">
                  {streaming.text ? <MarkdownMessage text={streaming.text} /> : <span className="dim">thinking…</span>}
                </div>
              </div>
            )}
          </div>
          <div className="composer case-composer">
            <textarea
              ref={inputRef}
              value={text}
              rows={2}
              onChange={(e) => onComposerChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (canSend) void send();
                }
              }}
              placeholder="Message the case assistant…"
              disabled={!activeCaseId}
            />
            {mode === 'ptt' && (
              <button
                type="button"
                className={pttHeld ? 'ptt held' : 'ptt'}
                disabled={!activeCaseId}
                title={micReady ? 'Push to Talk (or hold Space)' : 'Click to enable microphone, then push to talk'}
                onContextMenu={(e) => e.preventDefault()}
                onPointerDown={(e) => {
                  if (!activeCaseId || e.button !== 0) return;
                  e.preventDefault();
                  e.currentTarget.setPointerCapture(e.pointerId);
                  pttActivateOnPointerDown({
                    micReady: useStore.getState().audio.micReady,
                    startMic: () => controller.start(),
                    pttDown: () => controller.pttDown(),
                  }).catch(() => undefined);
                }}
                onPointerUp={(e) => {
                  e.preventDefault();
                  controller.pttUp();
                }}
                onPointerCancel={() => controller.pttUp()}
                onLostPointerCapture={() => controller.pttUp()}
              >
                {pttComposerLabel({ micReady, pttHeld })}
              </button>
            )}
            <button type="button" className="primary" onClick={() => void send()} disabled={!canSend}>
              Send
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => void controller.stopAssistant('lexical-interrupt')}
              disabled={!assistantBusy}
            >
              Stop
            </button>
          </div>
        </section>
      </div>

      <ConversationBrowser open={browserOpen} onClose={() => setBrowserOpen(false)} />
    </div>
  );
}
